// Upscale math (M13, PRODUCT_SPEC_V2 §2), pure and unit-tested. The network always enlarges 4×; the
// requested size (2×, 4×, or less when the 24 MP cap applies) is reached by area-averaging the 4× result,
// tile by tile, so the full 4× image never exists in memory.
//
// Tiling: the input is cut into CORE×CORE cores; each tile is the core plus OVERLAP px of real
// neighbourhood on every side (edge pixels repeated outside the image), so every tile has the same shape
// (one WebGPU pipeline). Only output pixels whose centre falls inside the tile's core are written, and
// each of them is computed entirely from that tile — there are no blends and no seams, as long as the
// overlap covers the network's border effect (measured in the M13 gate).

export const NET_SCALE = 4;
export const MAX_OUTPUT_PX = 24_000_000;
/** Below this the photo is "already as big as it can get". */
export const MIN_FACTOR = 1.25;
export const CORE = 256;
export const OVERLAP = 24;

export type Rect = { x0: number; y0: number; x1: number; y1: number };

/** The output size for a requested factor, capped at MAX_OUTPUT_PX; null when it cannot grow. */
export function targetSize(
  w: number,
  h: number,
  factor: number,
  maxPx = MAX_OUTPUT_PX,
): { width: number; height: number; factor: number } | null {
  const f = Math.min(factor, NET_SCALE, Math.sqrt(maxPx / (w * h)));
  if (!(f >= MIN_FACTOR)) return null;
  const width = Math.min(Math.floor(w * f + 1e-9), Math.round(w * f));
  const height = Math.min(Math.floor(h * f + 1e-9), Math.round(h * f));
  return { width, height, factor: width / w };
}

/** Cores covering the image, row by row. */
export function planTiles(w: number, h: number, core = CORE): Rect[] {
  const out: Rect[] = [];
  for (let y = 0; y < h; y += core)
    for (let x = 0; x < w; x += core)
      out.push({ x0: x, y0: y, x1: Math.min(w, x + core), y1: Math.min(h, y + core) });
  return out;
}

/** Tile edge length fed to the network. */
export const tileSize = (core = CORE, overlap = OVERLAP) => core + 2 * overlap;

/**
 * The network input for one tile: planar RGB 0..1, S×S, starting at (x0 − overlap, y0 − overlap), edge
 * pixels repeated outside the image. `rgba` is 8-bit interleaved.
 */
export function tileInput(
  rgba: ArrayLike<number>,
  w: number,
  h: number,
  r: Rect,
  core = CORE,
  overlap = OVERLAP,
): Float32Array {
  const S = tileSize(core, overlap);
  const out = new Float32Array(3 * S * S);
  const plane = S * S;
  for (let ty = 0; ty < S; ty++) {
    const sy = Math.min(h - 1, Math.max(0, r.y0 - overlap + ty));
    for (let tx = 0; tx < S; tx++) {
      const sx = Math.min(w - 1, Math.max(0, r.x0 - overlap + tx));
      const i = (sy * w + sx) * 4;
      const o = ty * S + tx;
      out[o] = rgba[i]! / 255;
      out[plane + o] = rgba[i + 1]! / 255;
      out[2 * plane + o] = rgba[i + 2]! / 255;
    }
  }
  return out;
}

/** Bilinear 4× of a planar tile (for the "strength" mix of models without an in-graph strength). */
export function bilinear4x(input: Float32Array, S: number): Float32Array {
  const O = S * NET_SCALE;
  const out = new Float32Array(3 * O * O);
  for (let c = 0; c < 3; c++) {
    const src = c * S * S,
      dst = c * O * O;
    for (let y = 0; y < O; y++) {
      const fy = Math.min(S - 1, Math.max(0, (y + 0.5) / NET_SCALE - 0.5));
      const y0 = Math.floor(fy),
        y1 = Math.min(S - 1, y0 + 1),
        wy = fy - y0;
      for (let x = 0; x < O; x++) {
        const fx = Math.min(S - 1, Math.max(0, (x + 0.5) / NET_SCALE - 0.5));
        const x0 = Math.floor(fx),
          x1 = Math.min(S - 1, x0 + 1),
          wx = fx - x0;
        const a = input[src + y0 * S + x0]! * (1 - wx) + input[src + y0 * S + x1]! * wx;
        const b = input[src + y1 * S + x0]! * (1 - wx) + input[src + y1 * S + x1]! * wx;
        out[dst + y * O + x] = a * (1 - wy) + b * wy;
      }
    }
  }
  return out;
}

/** Area weights of output pixel `i` over 4×-space cells: box [i·r, (i+1)·r) clipped to [lo, hi). */
function areaSpan(i: number, r: number, lo: number, hi: number): { start: number; w: number[] } {
  const a = Math.max(lo, i * r),
    b = Math.min(hi, (i + 1) * r);
  const start = Math.floor(a);
  const w: number[] = [];
  for (let k = start; k < b; k++) w.push(Math.min(b, k + 1) - Math.max(a, k));
  const sum = w.reduce((s, v) => s + v, 0) || 1;
  return { start, w: w.map((v) => v / sum) };
}

/**
 * Writes the output pixels owned by tile `r` into `out` (interleaved RGBA8, width W × H). `net` is the
 * network output for the tile (planar RGB, (S·4)²). Output pixel (X, Y) belongs to the tile whose core
 * contains its centre in input coordinates; its value is the area average of its footprint in 4× space.
 * Alpha is not touched (see `fillAlpha`).
 */
export function blitTile(
  out: Uint8Array | Uint8ClampedArray,
  W: number,
  H: number,
  srcW: number,
  srcH: number,
  net: Float32Array,
  r: Rect,
  core = CORE,
  overlap = OVERLAP,
) {
  const S = tileSize(core, overlap);
  const O = S * NET_SCALE;
  const plane = O * O;
  const rx = (srcW * NET_SCALE) / W; // 4×-space cells per output pixel
  const ry = (srcH * NET_SCALE) / H;
  const ox = (r.x0 - overlap) * NET_SCALE; // 4×-space origin of the tile
  const oy = (r.y0 - overlap) * NET_SCALE;
  const X0 = Math.max(0, Math.ceil((r.x0 * NET_SCALE) / rx - 0.5));
  const X1 = Math.min(W, Math.ceil((r.x1 * NET_SCALE) / rx - 0.5));
  const Y0 = Math.max(0, Math.ceil((r.y0 * NET_SCALE) / ry - 0.5));
  const Y1 = Math.min(H, Math.ceil((r.y1 * NET_SCALE) / ry - 0.5));
  const lo = 0;
  const hiX = O,
    hiY = O;
  const cols = [] as { start: number; w: number[] }[];
  for (let X = X0; X < X1; X++) {
    const s = areaSpan(X, rx, ox + lo, ox + hiX);
    cols.push({ start: s.start - ox, w: s.w });
  }
  for (let Y = Y0; Y < Y1; Y++) {
    const rs = areaSpan(Y, ry, oy + lo, oy + hiY);
    const rowStart = rs.start - oy;
    for (let X = X0; X < X1; X++) {
      const cs = cols[X - X0]!;
      let R = 0,
        G = 0,
        B = 0;
      for (let j = 0; j < rs.w.length; j++) {
        const wy = rs.w[j]!;
        const row = (rowStart + j) * O;
        for (let k = 0; k < cs.w.length; k++) {
          const wgt = wy * cs.w[k]!;
          const idx = row + cs.start + k;
          R += net[idx]! * wgt;
          G += net[plane + idx]! * wgt;
          B += net[2 * plane + idx]! * wgt;
        }
      }
      const o = (Y * W + X) * 4;
      out[o] = clamp8(R);
      out[o + 1] = clamp8(G);
      out[o + 2] = clamp8(B);
    }
  }
}
const clamp8 = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 255);

/** True when every pixel is opaque. */
export function isOpaque(rgba: ArrayLike<number>): boolean {
  for (let i = 3; i < rgba.length; i += 4) if (rgba[i] !== 255) return false;
  return true;
}

/** Alpha of the output: bilinear from the source alpha (the network sees RGB only); 255 when opaque. */
export function fillAlpha(
  out: Uint8Array | Uint8ClampedArray,
  W: number,
  H: number,
  src: ArrayLike<number>,
  w: number,
  h: number,
  opaque: boolean,
) {
  if (opaque) {
    for (let i = 3; i < out.length; i += 4) out[i] = 255;
    return;
  }
  for (let Y = 0; Y < H; Y++) {
    const fy = Math.min(h - 1, Math.max(0, ((Y + 0.5) * h) / H - 0.5));
    const y0 = Math.floor(fy),
      y1 = Math.min(h - 1, y0 + 1),
      wy = fy - y0;
    for (let X = 0; X < W; X++) {
      const fx = Math.min(w - 1, Math.max(0, ((X + 0.5) * w) / W - 0.5));
      const x0 = Math.floor(fx),
        x1 = Math.min(w - 1, x0 + 1),
        wx = fx - x0;
      const a = src[(y0 * w + x0) * 4 + 3]! * (1 - wx) + src[(y0 * w + x1) * 4 + 3]! * wx;
      const b = src[(y1 * w + x0) * 4 + 3]! * (1 - wx) + src[(y1 * w + x1) * 4 + 3]! * wx;
      out[(Y * W + X) * 4 + 3] = Math.round(a * (1 - wy) + b * wy);
    }
  }
}

/**
 * Seam measure for the gate: mean |Δluma| between horizontal neighbours across tile columns vs. between
 * all other horizontal neighbours (and the same for rows); `ratio` is the worse of the two. ≈ 1 = no seam.
 */
export function seamRatio(
  rgba: ArrayLike<number>,
  W: number,
  H: number,
  borderXs: number[],
  borderYs: number[],
): { across: number; elsewhere: number; ratio: number } {
  const lum = (i: number) => 0.2126 * rgba[i]! + 0.7152 * rgba[i + 1]! + 0.0722 * rgba[i + 2]!;
  const measure = (borders: Set<number>, horizontal: boolean) => {
    let sa = 0,
      na = 0,
      se = 0,
      ne = 0;
    for (let y = horizontal ? 0 : 1; y < H; y++)
      for (let x = horizontal ? 1 : 0; x < W; x++) {
        const a = (y * W + x) * 4;
        const b = horizontal ? a - 4 : a - W * 4;
        const d = Math.abs(lum(a) - lum(b));
        if (borders.has(horizontal ? x : y)) {
          sa += d;
          na++;
        } else {
          se += d;
          ne++;
        }
      }
    const across = na ? sa / na : 0,
      elsewhere = ne ? se / ne : 0;
    return {
      across,
      elsewhere,
      ratio: na === 0 ? 1 : elsewhere > 1e-9 ? across / elsewhere : across > 1e-9 ? Infinity : 1,
    };
  };
  const h = measure(new Set(borderXs), true),
    v = measure(new Set(borderYs), false);
  return h.ratio >= v.ratio ? h : v;
}

/** Output-pixel columns/rows where a new tile's ownership starts (for `seamRatio`). */
export function tileBorders(srcEdge: number, outEdge: number, core = CORE): number[] {
  const r = (srcEdge * NET_SCALE) / outEdge;
  const out: number[] = [];
  for (let x = core; x < srcEdge; x += core) out.push(Math.ceil((x * NET_SCALE) / r - 0.5));
  return out;
}
